import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import FastAPIClient from '../services/apiClient';
import { useToast } from '../hooks/useToast';
import { Plus, Minus, Store, DollarSign, Info } from 'lucide-react';
import { fetchStores } from '../apiHelper'; // Import fetchStores

// Initialize the API client
const client = new FastAPIClient();

/**
 * CreateStoreTaxes Component.
 * Allows users to create multiple store tax entries simultaneously.
 * Fetches stores dynamically and populates member_id from the logged-in user.
 * Designed with Tailwind CSS.
 */
const CreateStoreTaxes = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [ storeId, setStoreId ] = useState(location.state?.storeId);

    // State to hold user info (specifically users_id)
    const [loggedInUser, setLoggedInUser] = useState(null);
    // State to hold fetched stores for the dropdown
    const [storeOptions, setStoreOptions] = useState([]);
    const [loadingStores, setLoadingStores] = useState(true);

    // Initial state for a single tax entry
    const initialTax = {
        store_id: '', // Selected store ID
        tax_identifier: '',
        tax_description: '',
        tax_amount: '',
    };

    useEffect(() => {
        if(storeId) {
            initialTax.store_id = storeId;
        }
    },[storeId])

    // State to manage multiple tax entries
    const [storeTaxes, setStoreTaxes] = useState([initialTax]);
    const [errors, setErrors] = useState({}); // Stores errors, keyed by row index and field name
    const [submitStatus, setSubmitStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'

    // Effect to load user from local storage and fetch stores
    useEffect(() => {
        const loadUserDataAndStores = async () => {
            const lcUser = localStorage.getItem("user");
            if (lcUser) {
                const parsedUser = JSON.parse(lcUser);
                setLoggedInUser(parsedUser);

                // Fetch stores using the member_id
                try {
                    setLoadingStores(true);
                    const fetchedStores = await fetchStores(parsedUser); // Pass user object
                    const options = fetchedStores.map(store => ({
                        label: store.identifier,
                        value: store.store_id,
                    }));
                    setStoreOptions(options);
                } catch (err) {
                    console.error("Error loading stores:", err);
                    showToast(err.response?.data?.detail || "Failed to load stores. Please try again.", "error");
                } finally {
                    setLoadingStores(false);
                }
            } else {
                showToast("Please log in to define taxes.", "info");
                // Optionally redirect to login if no user is found
                navigate('/login');
            }
        };

        loadUserDataAndStores();
    }, [showToast]);

    // Handler for changes in individual tax input fields
    const handleTaxInputChange = useCallback((index, e) => {
        const { name, value } = e.target;
        const updatedTaxes = storeTaxes.map((tax, i) =>
            i === index ? { ...tax, [name]: value } : tax
        );
        setStoreTaxes(updatedTaxes);

        // Clear specific field error as user types
        if (errors[index] && errors[index][name]) {
            setErrors(prevErrors => {
                const newRowErrors = { ...prevErrors[index], [name]: '' };
                if (Object.values(newRowErrors).every(err => !err)) {
                    const newErrors = { ...prevErrors };
                    delete newErrors[index];
                    return newErrors;
                }
                return { ...prevErrors, [index]: newRowErrors };
            });
        }
    }, [storeTaxes, errors]);

    // Adds a new empty tax row
    const handleAddTaxRow = useCallback(() => {
        setStoreTaxes(prevTaxes => [
            ...prevTaxes,
            { ...initialTax }
        ]);
    }, [initialTax]);

    // Removes a tax row by index
    const handleRemoveTaxRow = useCallback((index) => {
        setStoreTaxes(prevTaxes => prevTaxes.filter((_, i) => i !== index));
        // Also remove errors for the deleted row
        setErrors(prevErrors => {
            const newErrors = { ...prevErrors };
            delete newErrors[index];
            return newErrors;
        });
    }, []);

    // Client-side validation for all tax entries
    const validateForm = useCallback(() => {
        let newErrors = {};
        let isValid = true;

        if (storeTaxes.length === 0) {
            newErrors.general = "At least one tax entry is required.";
            isValid = false;
        }

        storeTaxes.forEach((tax, index) => {
            let rowErrors = {};
            if (!tax.store_id) {
                rowErrors.store_id = 'Store is required.';
                isValid = false;
            }
            if (!tax.tax_identifier.trim()) {
                rowErrors.tax_identifier = 'Tax Identifier is required.';
                isValid = false;
            }
            if (tax.tax_amount === '' || isNaN(parseFloat(tax.tax_amount))) {
                rowErrors.tax_amount = 'Tax Amount is required and must be a number.';
                isValid = false;
            }
            if (Object.keys(rowErrors).length > 0) {
                newErrors[index] = rowErrors;
            }
        });

        setErrors(newErrors);
        return isValid;
    }, [storeTaxes]);

    // Form submission handler
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitStatus('loading');
        setErrors({}); // Clear previous errors

        if (!validateForm()) {
            setSubmitStatus('error');
            showToast("Please correct the errors in the form.", "error");
            return;
        }

        // Prepare payload
        const payload = storeTaxes.map(tax => ({
            store_id: parseInt(tax.store_id, 10),
            tax_identifier: tax.tax_identifier,
            tax_description: tax.tax_description || null, // Ensure null if empty
            tax_amount: parseFloat(tax.tax_amount),
        }));

        console.log('Submitting store taxes payload:', payload);

        try {
            // Assuming the backend endpoint for creating multiple store taxes is '/store/create_store_taxes'
            const response = await client.postData('/store/create-store-taxes', payload);
            console.log('Store taxes created successfully:', response);
            showToast("Store taxes created successfully!", "success");

            // Clear the form by resetting to a single empty tax
            setStoreTaxes([initialTax]);
            setSubmitStatus('success');
        } catch (err) {
            console.error('Failed to create store taxes:', err);
            const errorMessage = err.response?.data?.detail || `Failed to create store taxes: ${err.message || err}`;
            showToast(errorMessage, "error");
            setSubmitStatus('error');
        }
    };

    if (loadingStores) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <p className="text-text-secondary text-lg">Loading stores...</p>
            </div>
        );
    }

    if (!loggedInUser) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <Info size={32} className="mb-2" />
                <p className="text-lg mb-4">Please log in to create store taxes.</p>
                <Button onClick={() => navigate('/login')} variant="primary">
                    Go to Login
                </Button>
            </div>
        );
    }

    // If no stores are available, prompt user to create one
    if (storeOptions.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-4">
                <p className="text-gmail-red text-lg mb-4">
                    No stores found. Please create a store first before adding taxes.
                </p>
                <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={() => navigate('/stores/create')}
                    className="mt-4 mx-auto block"
                >
                    Go to Create Store
                </Button>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6">
            <Card className="p-6">
                <h2 className="text-2xl font-bold text-center text-text-primary mb-6">
                    Create New Store Taxes
                </h2>
                <p className="text-center text-text-secondary mb-8 text-sm">
                    Define tax rates for your stores. You can add multiple taxes at once.
                </p>

                <form onSubmit={handleSubmit} noValidate>
                    {storeTaxes.map((tax, index) => (
                        <div key={index} className="flex flex-col md:flex-row items-end gap-4 mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50 relative">
                            {/* Remove button for all but the first row (if only one row, disable) */}
                            {storeTaxes.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => handleRemoveTaxRow(index)}
                                    className="absolute top-2 right-2 p-1 rounded-full text-gray-500 hover:bg-red-100 hover:text-gmail-red
                                               focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gmail-red"
                                    aria-label={`Remove tax entry ${index + 1}`}
                                >
                                    <Minus size={20} />
                                </button>
                            )}
                            <div className="flex-1 w-full">
                                <SearchableSelectBox
                                    label={`Tax ${index + 1} Store`}
                                    id={`store_id-${index}`}
                                    name="store_id"
                                    value={tax.store_id}
                                    onChange={(e) => handleTaxInputChange(index, e)}
                                    options={storeOptions}
                                    placeholder="Select a store"
                                    required
                                    error={errors[index]?.store_id}
                                />
                                {errors[index]?.store_id && <p className="text-gmail-red text-xs mt-[-10px] mb-4">{errors[index].store_id}</p>}
                            </div>
                            <div className="flex-1 w-full">
                                <Input
                                    label={`Tax ${index + 1} Identifier`}
                                    id={`tax_identifier-${index}`}
                                    name="tax_identifier"
                                    type="text"
                                    placeholder="e.g., Sales Tax, VAT"
                                    value={tax.tax_identifier}
                                    onChange={(e) => handleTaxInputChange(index, e)}
                                    required
                                    error={errors[index]?.tax_identifier}
                                />
                                {errors[index]?.tax_identifier && <p className="text-gmail-red text-xs mt-[-10px] mb-4">{errors[index].tax_identifier}</p>}
                            </div>
                            <div className="flex-1 w-full">
                                <Input
                                    label={`Tax ${index + 1} Description (Optional)`}
                                    id={`tax_description-${index}`}
                                    name="tax_description"
                                    type="text"
                                    placeholder="Brief description of this tax"
                                    value={tax.tax_description}
                                    onChange={(e) => handleTaxInputChange(index, e)}
                                    error={errors[index]?.tax_description}
                                />
                                {errors[index]?.tax_description && <p className="text-gmail-red text-xs mt-[-10px] mb-4">{errors[index].tax_description}</p>}
                            </div>
                            <div className="flex-1 w-full">
                                <Input
                                    label={`Tax ${index + 1} Amount (%)`}
                                    id={`tax_amount-${index}`}
                                    name="tax_amount"
                                    type="number"
                                    step="0.01"
                                    placeholder="e.g., 5.00 for 5%"
                                    value={tax.tax_amount}
                                    onChange={(e) => handleTaxInputChange(index, e)}
                                    required
                                    error={errors[index]?.tax_amount}
                                />
                                {errors[index]?.tax_amount && <p className="text-gmail-red text-xs mt-[-10px] mb-4">{errors[index].tax_amount}</p>}
                            </div>
                        </div>
                    ))}

                    {errors.general && <p className="text-gmail-red text-sm text-center mb-4">{errors.general}</p>}

                    <div className="flex justify-between items-center mt-6">
                        <Button
                            type="button"
                            onClick={handleAddTaxRow}
                            variant="secondary"
                            className="flex items-center gap-2"
                        >
                            <Plus size={20} />
                            Add Another Tax
                        </Button>

                        <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            disabled={submitStatus === 'loading'}
                            className="flex items-center gap-2"
                        >
                            <DollarSign size={20} />
                            {submitStatus === 'loading' ? 'Creating Taxes...' : 'Create Taxes'}
                        </Button>
                    </div>
                </form>
            </Card>
        </div>
    );
};

export default CreateStoreTaxes;
